import { useState, useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import Layout from './components/layout/Layout'
import { api } from './utils/api'
import LoginPage from './pages/LoginPage'
import SsoCallbackPage from './pages/SsoCallbackPage'
import DashboardPage from './pages/DashboardPage'
import AssetsPage from './pages/AssetsPage'
import AssetDetailPage from './pages/AssetDetailPage'
import AssetFormPage from './pages/AssetFormPage'
import MasterDataPage from './pages/MasterDataPage'
import ImportPage from './pages/ImportPage'
import DeliveryListPage from './pages/DeliveryListPage'
import DeliveryFormPage from './pages/DeliveryFormPage'
import DeliveryPrintPage from './pages/DeliveryPrintPage'
import UsersPage from './pages/UsersPage'
import SettingsPage from './pages/SettingsPage'
import SoftwarePage from './pages/SoftwarePage'
import UserProfilePage from './pages/UserProfilePage'
import TicketsPage from './pages/TicketsPage'
import TicketDetailPage from './pages/TicketDetailPage'
import KnowledgeBasePage from './pages/KnowledgeBasePage'
import ChatbotPage from './pages/ChatbotPage'
import ZabbixAnalysisPage from './pages/ZabbixAnalysisPage'
import ZabbixReportPrintPage from './pages/ZabbixReportPrintPage'
import ServiceDeskReportPrintPage from './pages/ServiceDeskReportPrintPage'
import WorkOrdersPage from './pages/WorkOrdersPage'
import WorkOrderDetailPage from './pages/WorkOrderDetailPage'
import WorkOrderPrintPage from './pages/WorkOrderPrintPage'
import TicketApprovalPrintPage from './pages/TicketApprovalPrintPage'
import DailyCheckPage from './pages/DailyCheckPage'
import CheckReportPrintPage from './pages/CheckReportPrintPage'
import SystemStatusPage from './pages/SystemStatusPage'

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30000, retry: 1 } } })

function AppInner() {
  const location = useLocation()
  const [user, setUser] = useState<{username:string;role:string;companyId?:string|null;displayName?:string}|null>(() => {
    try { const u = localStorage.getItem('user'); return u ? JSON.parse(u) : null } catch { return null }
  })
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(() =>
    localStorage.getItem('activeCompanyId') || null
  )
  const [allowedMenus, setAllowedMenus] = useState<string[] | null>(null)
  // Whether the current (non-admin) user's role is configured (Settings → การเข้าถึงเมนู) to manage assets
  const [assetManagePermission, setAssetManagePermission] = useState(false)

  useEffect(() => {
    if (user) localStorage.setItem('user', JSON.stringify(user))
    else localStorage.removeItem('user')
  }, [user])

  const fetchMenuAccess = useCallback(async () => {
    if (!user) { setAllowedMenus(null); setAssetManagePermission(false); return }
    try {
      const r = await api.get('/auth/menu-access')
      setAllowedMenus(r.data.allowedMenus) // null = all menus
      setAssetManagePermission(!!r.data.canManageAssets)
    } catch {
      setAllowedMenus(null)
      setAssetManagePermission(false)
    }
  }, [user])

  useEffect(() => { fetchMenuAccess() }, [fetchMenuAccess])

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'
  const isAdmin = user?.role === 'ADMIN' || isSuperAdmin
  // ADMIN/SUPER_ADMIN always manage assets; other roles per the Settings-configured permission
  const canManageAssets = isAdmin || assetManagePermission
  // VIEWER lands on ServiceDesk after login; everyone else on the Dashboard
  const landingPath = user?.role === 'VIEWER' ? '/tickets' : '/dashboard'

  const handleSwitchCompany = (id: string | null) => {
    if (id) localStorage.setItem('activeCompanyId', id)
    else localStorage.removeItem('activeCompanyId')
    setActiveCompanyId(id)
    window.location.reload()
  }

  // Print routes — no Layout, no auth wall (token still needed for API)
  if (location.pathname.endsWith('/print')) {
    return (
      <Routes>
        <Route path="/deliveries/:id/print" element={<DeliveryPrintPage/>}/>
        <Route path="/workorders/:id/print" element={<WorkOrderPrintPage/>}/>
        <Route path="/checks/report/print" element={<CheckReportPrintPage/>}/>
        <Route path="/tickets/:id/approvals/print" element={<TicketApprovalPrintPage/>}/>
        <Route path="/zabbix/reports/:id/print" element={<ZabbixReportPrintPage/>}/>
        <Route path="/servicedesk/reports/:id/print" element={<ServiceDeskReportPrintPage/>}/>
        <Route path="*" element={<Navigate to="/dashboard"/>}/>
      </Routes>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={setUser}/>}/>
        <Route path="/sso-callback" element={<SsoCallbackPage onLogin={setUser}/>}/>
        <Route path="*" element={<Navigate to="/login"/>}/>
      </Routes>
    )
  }

  return (
    <Layout
      user={user}
      onLogout={() => { setUser(null); localStorage.removeItem('token'); localStorage.removeItem('activeCompanyId') }}
      isSuperAdmin={isSuperAdmin}
      activeCompanyId={activeCompanyId}
      onSwitchCompany={handleSwitchCompany}
      allowedMenus={allowedMenus}
      onMenuAccessChanged={fetchMenuAccess}
    >
      <Routes>
        <Route path="/" element={<Navigate to={landingPath}/>}/>
        <Route path="/dashboard" element={<DashboardPage isAdmin={isAdmin} allowedMenus={allowedMenus}/>}/>
        <Route path="/assets" element={<AssetsPage isAdmin={canManageAssets} isFullAdmin={isAdmin} allowedMenus={allowedMenus}/>}/>
        <Route path="/assets/new" element={canManageAssets ? <AssetFormPage/> : <Navigate to="/assets"/>}/>
        <Route path="/assets/:id" element={<AssetDetailPage isAdmin={isAdmin} canManageAssets={canManageAssets}/>}/>
        <Route path="/assets/:id/edit" element={canManageAssets ? <AssetFormPage/> : <Navigate to="/assets"/>}/>
        <Route path="/masterdata" element={<MasterDataPage isAdmin={isAdmin}/>}/>
        {/* Bookings & Reports moved into tabs on Assets / Dashboard — keep old links working */}
        <Route path="/bookings" element={<Navigate to="/assets?tab=bookings" replace/>}/>
        <Route path="/reports" element={<Navigate to="/dashboard" replace/>}/>
        <Route path="/import" element={canManageAssets ? <ImportPage/> : <Navigate to="/assets"/>}/>
        <Route path="/deliveries" element={<DeliveryListPage isAdmin={isAdmin}/>}/>
        <Route path="/deliveries/new" element={isAdmin ? <DeliveryFormPage/> : <Navigate to="/deliveries"/>}/>
        <Route path="/deliveries/:id/edit" element={isAdmin ? <DeliveryFormPage/> : <Navigate to="/deliveries"/>}/>
        <Route path="/software"      element={<SoftwarePage/>}/>
        <Route path="/tickets"       element={<TicketsPage/>}/>
        <Route path="/tickets/:id"   element={<TicketDetailPage/>}/>
        <Route path="/knowledge"     element={<KnowledgeBasePage isAdmin={isAdmin}/>}/>
        <Route path="/chatbot"       element={<ChatbotPage/>}/>
        <Route path="/zabbix"        element={<ZabbixAnalysisPage isAdmin={isAdmin}/>}/>
        <Route path="/dailycheck"    element={<DailyCheckPage isAdmin={isAdmin} role={user.role} isSuperAdmin={isSuperAdmin} activeCompanyId={activeCompanyId}/>}/>
        <Route path="/workorders"     element={<WorkOrdersPage canManageAssets={canManageAssets}/>}/>
        <Route path="/workorders/:id" element={<WorkOrderDetailPage canManageAssets={canManageAssets}/>}/>
        <Route path="/users"         element={isAdmin ? <UsersPage/>    : <Navigate to="/dashboard"/>}/>
        <Route path="/users/:id"     element={<UserProfilePage isAdmin={isAdmin}/>}/>
        <Route path="/settings"      element={isAdmin ? <SettingsPage/> : <Navigate to="/dashboard"/>}/>
        <Route path="/status"        element={isAdmin ? <SystemStatusPage/> : <Navigate to="/dashboard"/>}/>
        <Route path="/login" element={<Navigate to={landingPath}/>}/>
        <Route path="*" element={<Navigate to={landingPath}/>}/>
      </Routes>
    </Layout>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <AppInner/>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
